import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Phone, MapPin, Clock } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import SEOHead from '../components/SEOHead';

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const inquiry = searchParams.get('inquiry') || '';

  // Map inquiry types to titles and subtitles
  const inquiryInfo = {
    speaking: {
      title: "Speaking Engagement Inquiry",
      subtitle: "Let's discuss bringing EMBER's transformational leadership insights to your event"
    },
    consulting: {
      title: "Consulting Services Inquiry", 
      subtitle: "Explore how EMBER can support your organization's leadership development"
    },
    partnerships: {
      title: "Partnership Opportunity",
      subtitle: "Let's explore how we can create transformational impact together"
    },
    custom: {
      title: "Custom Program Development",
      subtitle: "Design a tailored leadership development program for your unique needs"
    }
  };

  const currentInquiry = inquiryInfo[inquiry] || {
    title: "Get in Touch",
    subtitle: "Let's discuss how EMBER can support your leadership journey"
  };

  return (
    <>
      <SEOHead 
        title="Contact EMBER - Leadership Development Consulting"
        description="Get in touch with EMBER for speaking engagements, consulting services, partnerships, and custom leadership development programs. Contact matt@emberimpact.com"
        keywords="ember contact, leadership consulting, speaking engagements, leadership development consulting, custom programs, partnerships, matt emberimpact"
        ogImage="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=2070&auto=format&fit=crop"
        canonicalUrl="/contact"
      />

      {/* Skip Links for Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-ember-fire text-white px-4 py-2 rounded-md z-50 font-medium"
        style={{ zIndex: 9999 }}
      >
        Skip to main content
      </a>
      
      <div className="min-h-screen bg-gradient-to-br from-neutral-warm via-white to-neutral-warm">
        <Navigation />
        
        <main id="main-content" role="main">
        
        {/* Hero Section */}
        <section className="pt-20 pb-16 relative overflow-hidden">
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <h1 className="text-5xl md:text-6xl font-bold text-neutral-charcoal mb-6 leading-tight">
                {currentInquiry.title}
              </h1>
              <p className="text-xl text-neutral-medium max-w-4xl mx-auto leading-relaxed">
                {currentInquiry.subtitle}
              </p>
            </div>
          </div>
        </section>

        {/* Contact Information Cards */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="bg-white rounded-2xl p-6 shadow-lg text-center border border-neutral-light">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-ember-fire" />
                <h3 className="text-xl font-bold text-neutral-charcoal mb-2">Send Message</h3>
                <p className="text-neutral-medium mb-3">Use the form below</p>
                <p className="text-neutral-dark font-medium">We'll respond quickly</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg text-center border border-neutral-light">
                <Clock className="w-12 h-12 mx-auto mb-4 text-ember-fire" />
                <h3 className="text-xl font-bold text-neutral-charcoal mb-2">Response Time</h3>
                <p className="text-neutral-medium mb-3">We respond quickly</p>
                <p className="text-neutral-dark font-medium">Within 24 hours</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg text-center border border-neutral-light">
                <MapPin className="w-12 h-12 mx-auto mb-4 text-ember-fire" />
                <h3 className="text-xl font-bold text-neutral-charcoal mb-2">Services</h3>
                <p className="text-neutral-medium mb-3">Available worldwide</p>
                <p className="text-neutral-dark font-medium">Virtual & In-Person</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <ContactForm 
              title={currentInquiry.title}
              subtitle={currentInquiry.subtitle}
              inquiry={inquiry}
            />
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gradient-to-br from-ember-depth to-ember-cool text-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-6">How We Can Work Together</h2>
              <p className="text-xl opacity-90 max-w-3xl mx-auto">
                EMBER offers multiple ways to bring transformational leadership development to your organization
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  title: 'Speaking',
                  description: 'Keynotes and workshops that inspire authentic leadership transformation',
                  link: '/contact?inquiry=speaking'
                },
                {
                  title: 'Consulting', 
                  description: 'Strategic guidance to develop leaders and transform organizational culture',
                  link: '/contact?inquiry=consulting'
                },
                {
                  title: 'Partnerships',
                  description: 'Collaborate with us to create innovative leadership development solutions',
                  link: '/contact?inquiry=partnerships'
                },
                {
                  title: 'Custom Programs',
                  description: 'Tailored leadership development programs designed for your unique needs',
                  link: '/contact?inquiry=custom'
                }
              ].map((service, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all">
                  <h3 className="font-bold text-lg mb-3">{service.title}</h3>
                  <p className="text-sm opacity-90 mb-4">{service.description}</p>
                  <Link 
                    to={service.link}
                    className="text-ember-spark hover:text-white transition-colors font-medium text-sm"
                  >
                    Learn More →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ContactPage;